import React, { useMemo, useState } from 'react';
import { Button, ButtonGroup, CheckPicker, DateRangePicker, Message, Panel, Placeholder, SelectPicker, Tag } from 'rsuite';
import { FilterField, FilterToolbar, renderSelectAllFooter } from '@nsi-advisory-services/components';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import {
  getCurrentPayPeriod,
  getPayPeriodOptions,
  toDateOnlyString,
  formatPeriodLabel,
} from '../utils/payPeriods';
import { getQuickDateRanges } from '../utils/dateRanges';
import PageHeader from '../components/shared/PageHeader';
import {
  useTimeCampPayPeriodReport,
  useSyncTimeCampPayPeriodReport,
} from '../hooks/useQueries';
import { renderEmployeeSelection } from '../components/shared/employeeSelection';

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

const formatSyncedAt = (isoValue) => {
  if (!isoValue) return null;
  const d = new Date(isoValue);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString();
};

const formatHours = (value) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return '0';
  const rounded = Math.round((num + Number.EPSILON) * 100) / 100;
  return String(rounded);
};

// Prefer the project path, then the task name, then a stable fallback.
const taskLabel = (task) => {
  if (task.breadcrumb) return task.breadcrumb;
  if (task.taskName) return task.taskName;
  if (task.taskId && task.taskId !== '0') return `Task ${task.taskId}`;
  return 'No task';
};

const buildHorizontalTaskChart = (tasks) => ({
  labels: tasks.map(taskLabel),
  datasets: [
    {
      label: 'Hours',
      data: tasks.map((t) => Number(t.totalHours) || 0),
      backgroundColor: 'rgba(25, 135, 84, 0.6)',
      borderColor: 'rgba(25, 135, 84, 1)',
      borderWidth: 1,
      borderRadius: 4,
      maxBarThickness: 36,
    },
  ],
});

const horizontalChartOptions = {
  indexAxis: 'y',
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { display: false },
    tooltip: { callbacks: { label: (ctx) => `${ctx.parsed.x} hrs` } },
  },
  scales: {
    x: { beginAtZero: true, title: { display: true, text: 'Hours' } },
    y: { ticks: { autoSkip: false } },
  },
};

// Draws each segment's share of its stacked bar (e.g. "72%") centered in the
// segment. Used for the billable vs non-billable stacked view.
const stackedPercentagePlugin = {
  id: 'stackedSegmentPercentages',
  afterDatasetsDraw(chart) {
    const { ctx } = chart;

    const totals = [];
    chart.data.datasets.forEach((dataset, datasetIndex) => {
      const meta = chart.getDatasetMeta(datasetIndex);
      if (meta.hidden) return;
      dataset.data.forEach((value, index) => {
        totals[index] = (totals[index] || 0) + (Number(value) || 0);
      });
    });

    ctx.save();
    ctx.font = 'bold 11px sans-serif';
    ctx.fillStyle = '#fff';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';

    chart.data.datasets.forEach((dataset, datasetIndex) => {
      const meta = chart.getDatasetMeta(datasetIndex);
      if (meta.hidden) return;
      meta.data.forEach((bar, index) => {
        const value = Number(dataset.data[index]) || 0;
        const total = totals[index] || 0;
        if (total <= 0 || value <= 0) return;

        const pct = Math.round((value / total) * 100);
        // Skip labels that won't fit in a thin segment.
        if (pct < 8 || Math.abs(bar.y - bar.base) < 16) return;

        const y = (bar.y + bar.base) / 2;
        ctx.fillText(`${pct}%`, bar.x, y);
      });
    });

    ctx.restore();
  },
};

export default function TimeCampReportPage() {
  // Oldest first so the latest periods sit at the bottom of the menu, matching
  // the timesheet validator's pay period picker.
  const payPeriodOptions = useMemo(() => [...getPayPeriodOptions()].reverse(), []);
  const currentPeriod = useMemo(() => getCurrentPayPeriod(), []);
  const currentPeriodValue = `${toDateOnlyString(currentPeriod.start)}|${toDateOnlyString(currentPeriod.end)}`;

  const [periodFilter, setPeriodFilter] = useState(currentPeriodValue);
  const [dateMode, setDateMode] = useState('payPeriod'); // 'payPeriod' | 'custom'
  const [customRange, setCustomRange] = useState(null); // [Date, Date] | null
  const [viewMode, setViewMode] = useState('employee'); // 'employee' | 'task'
  const [employeeMetric, setEmployeeMetric] = useState('total'); // 'total' | 'billable'
  const [selectedEmployeeId, setSelectedEmployeeId] = useState(null);

  const { start, end } = useMemo(() => {
    if (dateMode === 'custom') {
      if (Array.isArray(customRange) && customRange[0] && customRange[1]) {
        return { start: toDateOnlyString(customRange[0]), end: toDateOnlyString(customRange[1]) };
      }
      return { start: '', end: '' };
    }
    const [s, e] = String(periodFilter || '').split('|');
    return { start: s || '', end: e || '' };
  }, [dateMode, customRange, periodFilter]);

  const reportQuery = useTimeCampPayPeriodReport({ start, end }, { enabled: Boolean(start && end) });
  const syncMutation = useSyncTimeCampPayPeriodReport();

  const employees = useMemo(() => reportQuery.data?.employees || [], [reportQuery.data]);
  const lastSyncedAt = reportQuery.data?.lastSyncedAt || null;

  // Which employees to show. `null` means "all selected" (the default), so newly
  // synced employees stay visible until the user customizes the selection.
  const [selectedEmployeeIds, setSelectedEmployeeIds] = useState(null);

  const employeeOptions = useMemo(
    () => employees.map((emp) => ({ label: emp.name, value: emp.employeeId })),
    [employees]
  );

  const quickDateRanges = useMemo(() => getQuickDateRanges(), []);

  const allEmployeeIds = useMemo(() => employees.map((emp) => emp.employeeId), [employees]);
  const effectiveSelectedIds = selectedEmployeeIds ?? allEmployeeIds;

  const visibleEmployees = useMemo(() => {
    if (selectedEmployeeIds === null) return employees;
    const selected = new Set(selectedEmployeeIds);
    return employees.filter((emp) => selected.has(emp.employeeId));
  }, [employees, selectedEmployeeIds]);

  const isBillableMetric = employeeMetric === 'billable';

  const employeeChartData = useMemo(() => {
    const labels = visibleEmployees.map((emp) => emp.name);

    if (isBillableMetric) {
      return {
        labels,
        datasets: [
          {
            label: 'Billable',
            data: visibleEmployees.map((emp) => Number(emp.billableHours) || 0),
            backgroundColor: 'rgba(25, 135, 84, 0.75)',
            borderColor: 'rgba(25, 135, 84, 1)',
            borderWidth: 1,
            stack: 'hours',
            maxBarThickness: 48,
          },
          {
            label: 'Non-billable',
            data: visibleEmployees.map((emp) => Number(emp.nonBillableHours) || 0),
            backgroundColor: 'rgba(108, 117, 125, 0.65)',
            borderColor: 'rgba(108, 117, 125, 1)',
            borderWidth: 1,
            stack: 'hours',
            maxBarThickness: 48,
          },
        ],
      };
    }

    return {
      labels,
      datasets: [
        {
          label: 'Hours worked',
          data: visibleEmployees.map((emp) => (emp.totalHours === null ? 0 : emp.totalHours)),
          backgroundColor: 'rgba(13, 110, 253, 0.6)',
          borderColor: 'rgba(13, 110, 253, 1)',
          borderWidth: 1,
          borderRadius: 4,
          maxBarThickness: 48,
        },
      ],
    };
  }, [visibleEmployees, isBillableMetric]);

  const employeeChartOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    onClick: (_event, elements) => {
      if (!elements || elements.length === 0) return;
      const emp = visibleEmployees[elements[0].index];
      if (emp) setSelectedEmployeeId(emp.employeeId);
    },
    plugins: {
      legend: { display: isBillableMetric },
      tooltip: {
        callbacks: {
          label: (ctx) => {
            const value = ctx.parsed.y;
            if (!isBillableMetric) return `${value} hrs`;
            const total = (Number(visibleEmployees[ctx.dataIndex]?.totalHours) || 0);
            const pct = total > 0 ? Math.round((value / total) * 100) : 0;
            return `${ctx.dataset.label}: ${value} hrs (${pct}%)`;
          },
        },
      },
    },
    scales: {
      y: { beginAtZero: true, stacked: isBillableMetric, title: { display: true, text: 'Hours' } },
      x: { stacked: isBillableMetric, ticks: { autoSkip: false, maxRotation: 60, minRotation: 0 } },
    },
  }), [visibleEmployees, isBillableMetric]);

  // Company-wide aggregate across visible employees, grouped by task.
  const companyTasks = useMemo(() => {
    const map = new Map();
    for (const emp of visibleEmployees) {
      for (const task of emp.tasks || []) {
        const key = task.taskId || '0';
        const existing = map.get(key)
          || { taskId: key, taskName: task.taskName, breadcrumb: task.breadcrumb, totalHours: 0, entryCount: 0 };
        existing.totalHours += Number(task.totalHours) || 0;
        existing.entryCount += Number(task.entryCount) || 0;
        if (!existing.taskName && task.taskName) existing.taskName = task.taskName;
        if (!existing.breadcrumb && task.breadcrumb) existing.breadcrumb = task.breadcrumb;
        map.set(key, existing);
      }
    }
    return [...map.values()]
      .map((t) => ({ ...t, totalHours: Math.round((t.totalHours + Number.EPSILON) * 100) / 100 }))
      .sort((a, b) => b.totalHours - a.totalHours);
  }, [visibleEmployees]);

  const selectedEmployee = useMemo(
    () => visibleEmployees.find((emp) => emp.employeeId === selectedEmployeeId) || null,
    [visibleEmployees, selectedEmployeeId]
  );

  const selectedEmployeeTasks = useMemo(() => {
    if (!selectedEmployee) return [];
    return [...(selectedEmployee.tasks || [])].sort((a, b) => Number(b.totalHours) - Number(a.totalHours));
  }, [selectedEmployee]);

  const handleSync = () => {
    if (!start || !end) return;
    syncMutation.mutate({ start, end });
  };

  const handlePeriodChange = (nextValue) => {
    if (!nextValue) return;
    setPeriodFilter(String(nextValue));
    setSelectedEmployeeId(null);
  };

  const handleDateModeChange = (nextMode) => {
    setDateMode(nextMode);
    setSelectedEmployeeId(null);
  };

  const handleCustomRangeChange = (nextRange) => {
    setCustomRange(nextRange);
    setSelectedEmployeeId(null);
  };

  const periodLabel = useMemo(() => {
    if (!start || !end) return '';
    const startDate = new Date(`${start}T00:00:00`);
    const endDate = new Date(`${end}T00:00:00`);
    if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) return '';
    return formatPeriodLabel(startDate, endDate);
  }, [start, end]);

  const hasAnySyncedData = employees.some((emp) => emp.syncedAt);

  const renderTaskTable = (tasks) => (
    <div className="table-responsive">
      <table className="table table-sm align-middle mb-0">
        <thead>
          <tr>
            <th>Project / Task</th>
            <th className="text-end">Hours</th>
            <th className="text-end">Entries</th>
          </tr>
        </thead>
        <tbody>
          {tasks.map((task) => (
            <tr key={task.taskId}>
              <td>{taskLabel(task)}</td>
              <td className="text-end">{formatHours(task.totalHours)}</td>
              <td className="text-end">{task.entryCount ?? '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  return (
    <div className="bdc-page">
      <PageHeader
        crumbs={[{ label: 'Home', to: '/' }, { label: 'TimeCamp Report' }]}
        icon="bi-bar-chart"
        title="TimeCamp Hours Report"
      />

      <FilterToolbar>
          <FilterField label="Date">
            <ButtonGroup>
              <Button
                appearance={dateMode === 'payPeriod' ? 'primary' : 'default'}
                onClick={() => handleDateModeChange('payPeriod')}
              >
                Pay Period
              </Button>
              <Button
                appearance={dateMode === 'custom' ? 'primary' : 'default'}
                onClick={() => handleDateModeChange('custom')}
              >
                Custom Range
              </Button>
            </ButtonGroup>
          </FilterField>

          {dateMode === 'payPeriod' ? (
            <FilterField label="Pay Period" basis={240} max={320}>
              <SelectPicker
                data={payPeriodOptions}
                value={periodFilter}
                cleanable={false}
                searchable={false}
                block
                onChange={handlePeriodChange}
                renderMenuItem={(label, item) => (
                  <span className="d-flex align-items-center gap-2">
                    <span>{label}</span>
                    {item.value === currentPeriodValue && (
                      <Tag color="blue" size="sm">Current</Tag>
                    )}
                  </span>
                )}
                renderValue={(value, item) => (
                  <span className="d-flex align-items-center gap-2">
                    <span>{item?.label}</span>
                    {value === currentPeriodValue && (
                      <Tag color="blue" size="sm">Current</Tag>
                    )}
                  </span>
                )}
              />
            </FilterField>
          ) : (
            <FilterField label="Date Range" basis={240} max={320}>
              <DateRangePicker
                value={customRange}
                onChange={handleCustomRangeChange}
                placeholder="Select date range"
                format="yyyy-MM-dd"
                block
                ranges={quickDateRanges}
              />
            </FilterField>
          )}

          <FilterField label="Breakdown">
            <ButtonGroup>
              <Button
                appearance={viewMode === 'employee' ? 'primary' : 'default'}
                onClick={() => setViewMode('employee')}
              >
                By Employee
              </Button>
              <Button
                appearance={viewMode === 'task' ? 'primary' : 'default'}
                onClick={() => setViewMode('task')}
              >
                By Task
              </Button>
            </ButtonGroup>
          </FilterField>

          <FilterField label="Employees" basis={240} max={320}>
            <CheckPicker
              data={employeeOptions}
              countable={false}
              renderValue={renderEmployeeSelection(employeeOptions)}
              value={effectiveSelectedIds}
              onChange={(value) => setSelectedEmployeeIds(value || [])}
              placeholder="All employees"
              block
              preventOverflow
              renderExtraFooter={renderSelectAllFooter(
                employeeOptions,
                effectiveSelectedIds,
                setSelectedEmployeeIds,
              )}
            />
          </FilterField>

          <FilterField
            label={lastSyncedAt ? `Last synced ${formatSyncedAt(lastSyncedAt)}` : 'Not synced yet'}
          >
            <Button
              appearance="primary"
              onClick={handleSync}
              loading={syncMutation.isPending}
              disabled={!start || !end}
            >
              Sync from TimeCamp
            </Button>
          </FilterField>
      </FilterToolbar>

      {syncMutation.isError ? (
        <Message type="error" showIcon>
          Could not sync TimeCamp hours: {String(syncMutation.error?.message || syncMutation.error)}
        </Message>
      ) : null}

      {reportQuery.isError ? (
        <Message type="error" showIcon>
          Could not load report: {String(reportQuery.error?.message || reportQuery.error)}
        </Message>
      ) : null}

      {!start || !end ? (
        <Message type="info" showIcon>
          Select a date range to view the report.
        </Message>
      ) : reportQuery.isLoading ? (
        <Panel bordered>
          <Placeholder.Graph active height={360} />
        </Panel>
      ) : employees.length === 0 ? (
        <Panel bordered>
          <div className="text-muted">No active employees found.</div>
        </Panel>
      ) : !hasAnySyncedData ? (
        <Message type="info" showIcon>
          No cached TimeCamp data for this date range yet. Click “Sync from TimeCamp” to pull it.
        </Message>
      ) : visibleEmployees.length === 0 ? (
        <Message type="info" showIcon>
          No employees selected. Pick employees in the “Employees” filter to show data.
        </Message>
      ) : viewMode === 'employee' ? (
        <>
          <Panel bordered header={periodLabel ? `Hours worked — ${periodLabel}` : 'Hours worked'}>
            <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-2">
              <div className="text-muted small">Click an employee’s bar to see their task breakdown.</div>
              <ButtonGroup size="sm">
                <Button
                  appearance={employeeMetric === 'total' ? 'primary' : 'default'}
                  onClick={() => setEmployeeMetric('total')}
                >
                  Total Hours
                </Button>
                <Button
                  appearance={employeeMetric === 'billable' ? 'primary' : 'default'}
                  onClick={() => setEmployeeMetric('billable')}
                >
                  Billable Split
                </Button>
              </ButtonGroup>
            </div>
            <div style={{ height: 420 }}>
              <Bar
                data={employeeChartData}
                options={employeeChartOptions}
                plugins={isBillableMetric ? [stackedPercentagePlugin] : []}
              />
            </div>
          </Panel>

          {selectedEmployee ? (
            <Panel
              bordered
              header={`Task breakdown — ${selectedEmployee.name} (${formatHours(selectedEmployee.totalHours || 0)} hrs)`}
            >
              {selectedEmployeeTasks.length === 0 ? (
                <div className="text-muted">No task data for this employee in this period.</div>
              ) : (
                <>
                  <div style={{ height: Math.max(160, selectedEmployeeTasks.length * 44) }}>
                    <Bar data={buildHorizontalTaskChart(selectedEmployeeTasks)} options={horizontalChartOptions} />
                  </div>
                  <div className="mt-3">{renderTaskTable(selectedEmployeeTasks)}</div>
                </>
              )}
            </Panel>
          ) : null}
        </>
      ) : (
        <Panel bordered header={periodLabel ? `Hours by task — ${periodLabel}` : 'Hours by task'}>
          {companyTasks.length === 0 ? (
            <div className="text-muted">No task data for this period.</div>
          ) : (
            <>
              <div style={{ height: Math.max(200, companyTasks.length * 44) }}>
                <Bar data={buildHorizontalTaskChart(companyTasks)} options={horizontalChartOptions} />
              </div>
              <div className="mt-3">{renderTaskTable(companyTasks)}</div>
            </>
          )}
        </Panel>
      )}
    </div>
  );
}
